import { CheckCircle, Ship, MapPin, Anchor, Circle, Truck, AlertCircle } from "lucide-react";

interface StatusUpdate {
  update_id: number;
  status: string;
  location: string;
  description: string;
  timestamp: string;
}

interface ShipmentJourneyProps {
  updates: StatusUpdate[];
}

export const ShipmentJourney = ({ updates = [] }: ShipmentJourneyProps) => {
  // Define an icon and color mapping for different statuses based on design
  const getStatusConfig = (status: string, index: number, isLast: boolean) => {
    const s = status.toLowerCase();
    
    // Default config
    let Icon = CheckCircle;
    let colorClass = "bg-green-500 text-white";
    let lineClass = "border-green-500";
    let iconSize = 16;
    let isPending = false;

    if (s.includes('hold')) {
      Icon = AlertCircle;
      colorClass = "bg-amber-500 text-white";
      lineClass = "border-amber-500";
    } else if (s.includes('transit') || s.includes('departed')) {
      Icon = Ship;
      colorClass = "bg-blue-600 text-white";
      lineClass = "border-blue-600";
    } else if (s.includes('delivery') || s.includes('delivered')) {
      Icon = Truck;
      if (isLast) {
        colorClass = "bg-gray-100 text-gray-400"; // Pending final delivery
        lineClass = "border-dashed border-gray-300";
        isPending = true;
      }
    } else if (s.includes('port') || s.includes('arrival')) {
      Icon = Anchor;
      colorClass = "bg-white text-gray-400 border-2 border-gray-200";
      lineClass = "border-dashed border-gray-300";
      isPending = true;
    } else if (s.includes('exception')) {
      Icon = Circle;
      colorClass = "bg-red-500 text-white";
      lineClass = "border-red-500";
    } else {
      // Completed past steps
      Icon = CheckCircle;
      colorClass = "bg-green-500 text-white";
      lineClass = "border-green-500";
    }

    return { Icon, colorClass, lineClass, iconSize, isPending };
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 h-full">
      <h2 className="text-xs font-bold text-slate-800 tracking-wide uppercase mb-4">Shipment Journey</h2>
      
      {updates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No status updates available</p>
        </div>
      ) : (
        <div className="relative pl-3 mt-2">
          {updates.map((update, index) => (
            <div key={update.update_id} className="relative pb-6 last:pb-0 pl-6">
              {/* Timeline line */}
              {index !== updates.length - 1 && (
                <div className={`absolute top-6 left-0 w-0 h-full border-l-2 -ml-[1px] ${getStatusConfig(update.status, index, false).lineClass}`} />
              )}
              
              {/* Timeline Icon */}
              <div className={`absolute top-0 -left-[10px] w-5 h-5 rounded-full flex items-center justify-center z-10 ${getStatusConfig(update.status, index, false).colorClass}`}>
                {(() => {
                  const ConfigIcon = getStatusConfig(update.status, index, false).Icon;
                  return <ConfigIcon size={12} />;
                })()}
              </div>
              
              <div className="flex justify-between items-start">
                <div>
                  <h4 className={`font-bold text-sm ${getStatusConfig(update.status, index, false).isPending ? 'text-gray-500' : (getStatusConfig(update.status, index, false).colorClass.includes('blue') ? 'text-blue-700' : 'text-gray-900')}`}>
                    {update.status}
                  </h4>
                  {update.description && (
                    <p className="text-[10px] text-gray-500 mt-0.5 max-w-[200px]">{update.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-500">{new Date(update.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - {new Date(update.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
                  <p className="text-[10px] text-gray-500">{update.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
